/**
 * SectionList — Container fuer die Sektionen (Phase 10 + Phase 11).
 *
 * Paket-Tab: stuck → carrier → waiting → manualOnly → done (5 Sektionen)
 * Lieferanten-Tab: huck → helo → glawel → sporttore (4 Sektionen, orderStatusId ASC)
 *
 * stuck und manualOnly werden NICHT gerendert wenn stats.trackingStatus.stuck/manual_only === 0.
 * Lieferanten-Sektionen immer anzeigen (auch wenn 0 — operative Sicht).
 *
 * Carrier-Sub-Filter (dhlStatus) wird client-side angewendet — filtert die geladenen Orders der
 * Carrier-Sektion nach lastInternalStatus.
 *
 * Imperative API: scrollToSection(sectionId) via forwardRef + useImperativeHandle.
 */

import { forwardRef, useImperativeHandle, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import SectionHeader from './SectionHeader';
import OrderCard from './OrderCard';
import type { SectionId, VersandKategorie } from '../lib/sectionQueryBuilder';
import { isSupplierSection, isSupplierDoneSection } from '../lib/sectionQueryBuilder';
import type {
  DashboardStatsResponse,
  InternalStatusKey,
} from '../lib/dashboardStatsMapper';
import { INTERNAL_STATUS_LABELS, SUPPLIER_LABELS } from '../lib/dashboardStatsMapper';
import { groupOrders, type OrderListItem } from '../lib/groupOrders';
import type { CollapsedState } from '../lib/sectionStorage';

export interface SectionState {
  orders: OrderListItem[];
  page: number;
  totalPages: number;
  total: number;
  loading: boolean;
  error: string | null;
  initialized: boolean;       // false bis erstes Expand-Fetch
}

export interface SubFilterState {
  dhlStatus: InternalStatusKey | null;
}

export interface SectionListProps {
  sectionsState: Partial<Record<SectionId, SectionState>>;
  collapsed: CollapsedState;
  kategorie: VersandKategorie;
  stats: DashboardStatsResponse | null;
  subFilter: SubFilterState;
  onToggleCollapsed: (sectionId: SectionId) => void;
  onPageChange: (sectionId: SectionId, page: number) => void;
  onClearSubFilter: () => void;
  onSelectOrder: (orderId: number) => void;
  /** Callback wenn eine Karte manuell abgeschlossen wurde (Parent triggert reload). */
  onOrderClosed?: (orderId: number) => void;
  /** 20.08.2026: Callback wenn der Geklärt-Haken (Rücksendungs-Reiter) persistiert wurde. */
  onGeklaertToggled?: (orderId: number) => void;
  /**
   * Phase 17: Wenn gesetzt, rendert SectionList NUR die angegebene Sektion ohne SectionHeader
   * (Header wird durch externe SectionTabs ersetzt). Wenn null/undefined: alle Sektionen
   * wie bisher (Followup-Tab nutzt das).
   */
  activeSectionId?: SectionId | null;
}

export interface SectionListHandle {
  scrollToSection: (sectionId: SectionId) => void;
}

// Phase 10: Paket-Tab Sektion-Reihenfolge (bestehend, unveraendert)
const PAKET_SECTION_ORDER: SectionId[] = ['stuck', 'carrier', 'neverShipped', 'waiting', 'manualOnly', 'done'];

// Phase 11+15+16: Lieferanten-Tab Sektion-Reihenfolge — pro Lieferant erst Offen,
// dann Abgeschlossen (Done) als zugeklappte Sub-Sektion, WFF zuletzt.
const LIEFERANTEN_SECTION_ORDER: SectionId[] = [
  'huck', 'huckDone',
  'helo', 'heloDone',
  'glawel', 'glawelDone',
  'sporttore', 'sporttoreDone',
  'wff', 'wffDone',
];

// Phase 14: Nachverfolgungs-Tab — eine einzige Sektion
const FOLLOWUP_SECTION_ORDER: SectionId[] = ['followup'];

/** Tab-spezifische SECTION_ORDER */
function getSectionOrder(kategorie: VersandKategorie): SectionId[] {
  if (kategorie === 'lieferanten') return LIEFERANTEN_SECTION_ORDER;
  if (kategorie === 'followup') return FOLLOWUP_SECTION_ORDER;
  return PAKET_SECTION_ORDER;
}

/** Welche Sektionen werden gerendert? — Stuck + ManualOnly nur wenn total > 0. Lieferanten immer. */
function sectionIsVisible(sectionId: SectionId, stats: DashboardStatsResponse | null): boolean {
  // Followup ist seine eigene Tab-Sektion — immer rendern
  if (sectionId === 'followup') return true;
  if (!stats) {
    // Ohne Stats: carrier, waiting, done immer zeigen; stuck + manualOnly verstecken
    // Lieferanten-Sektionen immer zeigen
    if (isSupplierSection(sectionId)) return true;
    if (isSupplierDoneSection(sectionId)) return true;
    if (sectionId === 'wffDone') return true;
    return sectionId === 'carrier' || sectionId === 'waiting' || sectionId === 'done';
  }
  // Lieferanten-Sektionen: immer zeigen (auch wenn 0 — operative Sicht, Anzahl im Header sichtbar)
  if (isSupplierSection(sectionId)) return true;
  // Phase 16: Lieferanten-Done nur zeigen wenn > 0 (sonst nutzlose Sektion)
  if (isSupplierDoneSection(sectionId)) {
    if (sectionId === 'huckDone')      return (stats.supplierCompleted?.huck ?? 0) > 0;
    if (sectionId === 'heloDone')      return (stats.supplierCompleted?.helo ?? 0) > 0;
    if (sectionId === 'glawelDone')    return (stats.supplierCompleted?.glawel ?? 0) > 0;
    if (sectionId === 'sporttoreDone') return (stats.supplierCompleted?.sporttore ?? 0) > 0;
  }
  if (sectionId === 'stuck') return stats.trackingStatus.stuck > 0;
  if (sectionId === 'manualOnly') return stats.trackingStatus.manual_only > 0;
  return true;
}

/** Total fuer SectionHeader — kommt aus stats (autoritativ), Fallback sectionsState. */
function sectionTotal(
  sectionId: SectionId,
  stats: DashboardStatsResponse | null,
  state: SectionState,
): number {
  // Followup: dashboardStats hat keine eigene Zahl fuer followup —
  // fallback auf state.total (von /list-Response)
  if (sectionId === 'followup') return state.total;
  if (!stats) return state.total;
  // Phase 11: Lieferanten-Sektionen aus supplierDistribution
  if (sectionId === 'huck')      return stats.supplierDistribution.huck;
  if (sectionId === 'helo')      return stats.supplierDistribution.helo;
  if (sectionId === 'glawel')    return stats.supplierDistribution.glawel;
  if (sectionId === 'sporttore') return stats.supplierDistribution.sporttore;
  // Phase 16: Lieferanten-Done aus supplierCompleted
  if (sectionId === 'huckDone')      return stats.supplierCompleted?.huck ?? state.total;
  if (sectionId === 'heloDone')      return stats.supplierCompleted?.helo ?? state.total;
  if (sectionId === 'glawelDone')    return stats.supplierCompleted?.glawel ?? state.total;
  if (sectionId === 'sporttoreDone') return stats.supplierCompleted?.sporttore ?? state.total;
  // Phase 15 / 2026-07-27: WFF offen + abgeschlossen (manufacturer-basiert)
  if (sectionId === 'wff')       return stats.supplierDistribution.wff ?? state.total;
  if (sectionId === 'wffDone')   return stats.supplierCompleted?.wff ?? state.total;
  // Paket-Tab
  const ts = stats.trackingStatus;
  switch (sectionId) {
    case 'stuck':      return ts.stuck;
    case 'carrier':    return ts.carrier;
    case 'neverShipped': return ts.neverShipped;
    case 'waiting':    return ts.open + ts.awaiting_package;
    case 'manualOnly': return ts.manual_only;
    case 'done':       return ts.done + ts.closed_manually;
  }
  return state.total;
}

/** Sektion-Header-Label — Lieferanten-Sektionen nutzen SUPPLIER_LABELS. */
function sectionLabel(sectionId: SectionId): string {
  if (sectionId === 'huck')      return SUPPLIER_LABELS.huck;
  if (sectionId === 'helo')      return SUPPLIER_LABELS.helo;
  if (sectionId === 'glawel')    return SUPPLIER_LABELS.glawel;
  if (sectionId === 'sporttore') return SUPPLIER_LABELS.sporttore;
  return sectionId; // SectionHeader verwendet sectionId als Label-Key intern
}

const SectionList = forwardRef<SectionListHandle, SectionListProps>(function SectionList(
  {
    sectionsState,
    collapsed,
    kategorie,
    stats,
    subFilter,
    onToggleCollapsed,
    onPageChange,
    onClearSubFilter,
    onSelectOrder,
    onOrderClosed,
    onGeklaertToggled,
    activeSectionId,
  },
  ref,
) {
  // KRITISCH: sectionRefs MUSS alle 16 SectionId-Keys enthalten
  // (6 Paket + 5 Lieferanten-Offen + 4 Lieferanten-Done + 1 Followup)
  const sectionRefs = useRef<Record<SectionId, HTMLDivElement | null>>({
    stuck: null,
    carrier: null,
    neverShipped: null,    // Phase 18
    waiting: null,
    manualOnly: null,
    done: null,
    huck: null,            // Phase 11
    helo: null,            // Phase 11
    glawel: null,          // Phase 11
    sporttore: null,       // Phase 11
    wff: null,             // Phase 15
    huckDone: null,        // Phase 16
    heloDone: null,        // Phase 16
    glawelDone: null,      // Phase 16
    sporttoreDone: null,   // Phase 16
    wffDone: null,         // 2026-07-27: WFF-Abgeschlossen
    followup: null,        // Phase 14
  });

  useImperativeHandle(ref, () => ({
    scrollToSection(sectionId: SectionId) {
      const el = sectionRefs.current[sectionId];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  }), []);

  // Phase 17: Tabbed-Modus — nur die aktive Sektion rendern, ohne SectionHeader (Tabs ersetzen ihn).
  const tabbedMode = activeSectionId != null;
  const sectionOrder = tabbedMode ? [activeSectionId!] : getSectionOrder(kategorie);

  return (
    <div className="flex flex-col gap-4">
      {sectionOrder.map((sectionId) => {
        if (!sectionIsVisible(sectionId, stats)) return null;

        const state = sectionsState[sectionId];
        if (!state) return null;

        // In tabbed mode: aktive Sektion ist immer "expanded" (kein Toggle).
        const isCollapsed = tabbedMode ? false : collapsed[sectionId];
        const total = sectionTotal(sectionId, stats, state);

        // Carrier-Sub-Filter: client-side filter auf lastInternalStatus
        let visibleOrders = state.orders;
        let chip: { label: string; onClear: () => void } | null = null;
        if (sectionId === 'carrier' && subFilter.dhlStatus) {
          visibleOrders = state.orders.filter(
            (o) => o.lastInternalStatus === subFilter.dhlStatus,
          );
          chip = {
            label: `DHL: ${INTERNAL_STATUS_LABELS[subFilter.dhlStatus]}`,
            onClear: onClearSubFilter,
          };
        }

        const grouped = groupOrders(visibleOrders);

        return (
          <div
            key={sectionId}
            ref={(el) => {
              sectionRefs.current[sectionId] = el;
            }}
            data-section-id={sectionId}
          >
            {/* Phase 17: SectionHeader nur im stacked-Modus — im tabbed-Modus ersetzen Tabs den Header */}
            {!tabbedMode && (
              <SectionHeader
                sectionId={sectionId}
                total={total}
                loaded={state.initialized ? state.orders.length : null}
                collapsed={isCollapsed}
                loading={state.loading}
                subFilterChip={chip}
                onToggle={() => onToggleCollapsed(sectionId)}
              />
            )}

            {!isCollapsed && (
              <div className={tabbedMode ? '' : 'mt-2'}>
                {/* Error-State */}
                {state.error && (
                  <div className="text-sm text-red-700 italic px-3 py-2 bg-red-50 border border-red-200 rounded">
                    {state.error}
                  </div>
                )}

                {/* Loading-State (nur bei initialem Laden) */}
                {!state.initialized && state.loading && (
                  <div className="text-sm text-gray-500 italic text-center py-6">
                    Lade…
                  </div>
                )}

                {/* Empty-State */}
                {state.initialized && visibleOrders.length === 0 && (
                  <div className="text-sm text-gray-500 italic text-center py-6 border border-dashed border-[#DAD9D9] rounded">
                    {subFilter.dhlStatus && sectionId === 'carrier'
                      ? `Keine Carrier-Orders mit Status "${INTERNAL_STATUS_LABELS[subFilter.dhlStatus]}" auf dieser Seite`
                      : 'Keine Auftraege in dieser Sektion'}
                  </div>
                )}

                {/* Karten-Grid */}
                {visibleOrders.length > 0 && (
                  <div className="flex flex-col gap-3">
                    {grouped.map((group) => (
                      <OrderCard
                        key={group.main.id}
                        group={group}
                        kategorie={kategorie}
                        onSelect={onSelectOrder}
                        onClosed={onOrderClosed}
                        // 20.08.2026: Geklärt-Haken nur im Rücksendungs-Reiter
                        showGeklaertToggle={sectionId === 'ruecksendung'}
                        onGeklaertToggled={onGeklaertToggled}
                      />
                    ))}
                  </div>
                )}

                {/* Pagination pro Sektion — nur wenn totalPages > 1 (ungefilterte Server-Antwort) */}
                {state.totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 pt-3">
                    <button
                      type="button"
                      onClick={() => onPageChange(sectionId, Math.max(1, state.page - 1))}
                      disabled={state.page <= 1 || state.loading}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[#DAD9D9] rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" /> Vorige
                    </button>
                    <span className="text-sm text-[#1A1A1A]">
                      Seite {state.page} / {state.totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onPageChange(sectionId, Math.min(state.totalPages, state.page + 1))
                      }
                      disabled={state.page >= state.totalPages || state.loading}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[#DAD9D9] rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Naechste <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

export default SectionList;
